import type { Dispatch, KeyboardEvent, SetStateAction } from 'react';
import type { RangeSelectionProps, SingleSelectionProps } from '../BasicDatePicker';

export const handleAutoFormatOnBlur = (
    dateString: string,
    setInputValue: Dispatch<SetStateAction<string>>,
    setDate: Dispatch<SetStateAction<Date | null>>,
    parseDateFunc: (dateString: string) => Date | null,
) => {
    const cleanedInput = dateString.replace(/[^\d]/g, '');

    if (cleanedInput.length === 6 || cleanedInput.length === 8) {
        let year = cleanedInput.slice(4, 6);
        if (cleanedInput.length === 6) {
            year = `20${year}`;
        } else {
            year = cleanedInput.slice(4, 8);
        }
        const formattedInput = `${cleanedInput.slice(0, 2)}.${cleanedInput.slice(2, 4)}.${year}`;
        setInputValue(formattedInput);
        const parsedDate = parseDateFunc(formattedInput);
        if (parsedDate) {
            setDate(parsedDate);
        }
    } else {
        setInputValue(dateString);
    }
};

type DateChangeProps = Pick<SingleSelectionProps, 'onDateChange' | 'useRange'> | Pick<RangeSelectionProps, 'onDateChange' | 'useRange'>;

export const handleDateInputChange = (
    dateString: string,
    setInputValue: Dispatch<SetStateAction<string>>,
    setDate: Dispatch<SetStateAction<Date | null>>,
    parseDateFunc: (dateString: string) => Date | null,
    isEndDate: boolean,
    setEndDateFirst: Dispatch<SetStateAction<boolean>>,
    setIsStartDateFocused: Dispatch<SetStateAction<boolean>>,
    setIsEndDateFocused: Dispatch<SetStateAction<boolean>>,
    startDate: Date | null,
    endDate: Date | null,
    props: DateChangeProps,
) => {
    setInputValue(dateString);

    if (dateString.trim() === '') {
        setDate(null);

        if (props.useRange) {
            (props as Pick<RangeSelectionProps, 'onDateChange'>).onDateChange?.({
                startDate: isEndDate ? startDate : null,
                endDate: isEndDate ? null : endDate,
            });
        } else {
            (props as Pick<SingleSelectionProps, 'onDateChange'>).onDateChange?.({
                startDate: null,
            });
        }

        if (isEndDate) {
            setEndDateFirst(false);
            setIsEndDateFocused(true);
        } else {
            setIsStartDateFocused(true);
        }
        return;
    }

    // Validate format (e.g., dd.mm.yyyy for Norwegian dates)
    const validFormatRegex = /^\d{2}\.\d{2}\.\d{4}$/;
    if (validFormatRegex.test(dateString)) {
        const parsedDate = parseDateFunc(dateString);

        if (parsedDate) {
            setDate(parsedDate);

            if (props.useRange) {
                (props as Pick<RangeSelectionProps, 'onDateChange'>).onDateChange?.({
                    startDate: isEndDate ? startDate : parsedDate,
                    endDate: isEndDate ? parsedDate : endDate,
                });
            } else {
                (props as Pick<SingleSelectionProps, 'onDateChange'>).onDateChange?.({
                    startDate: parsedDate,
                });
            }
        } else {
            setDate(null);
        }
    } else {
        setDate(null);
    }
};

export const handleDateInputKeyDown = (
    event: KeyboardEvent<HTMLInputElement>,
    value: string,
    isEndDate: boolean,
    props: DateChangeProps,
    setStartDate: (date: Date | null) => void,
    setEndDate: (date: Date | null) => void,
    showCalendar?: (isEndDate: boolean) => void,
    setIsStartDateFocused?: (focused: boolean) => void,
    setIsEndDateFocused?: (focused: boolean) => void,
) => {
    // Handle Backspace when input is empty
    if (event.key === 'Backspace' && value === '') {
        if (props.useRange) {
            if (isEndDate) {
                setEndDate(null);
                (props as RangeSelectionProps).onDateChange?.({ startDate: null, endDate: null });
            } else {
                setStartDate(null);
                (props as RangeSelectionProps).onDateChange?.({ startDate: null, endDate: null });
            }
        } else {
            setStartDate(null);
            (props as SingleSelectionProps).onDateChange?.({ startDate: null });
        }
        return;
    }

    // Handle Enter & Space key behavior
    if (['Enter', ' '].includes(event.key)) {
        event.preventDefault();
        showCalendar?.(isEndDate);
        setIsStartDateFocused?.(!isEndDate);
        setIsEndDateFocused?.(isEndDate);
    }
};
